import React from 'react';
import styled from 'styled-components';

export type PromptChip = {
  id: string;
  label: string;
  icon?: React.ReactNode;
  onRemove?: () => void;
};

export type PromptComposerProps = {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  rows?: number;
  chips?: PromptChip[];
  trailing?: React.ReactNode;
  charLimit?: number;
  disabled?: boolean;
  onSubmit?: () => void;
  className?: string;
};

const Root = styled.div<{ $disabled: boolean }>`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-3);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
  border: 1px solid var(--color-border-subtle);
  transition: border-color var(--duration-base) var(--ease-standard),
    box-shadow var(--duration-base) var(--ease-standard);
  opacity: ${(p) => (p.$disabled ? 0.6 : 1)};

  &:focus-within {
    border-color: var(--color-accent);
    box-shadow: 0 0 0 3px var(--color-focus-ring);
  }
`;

const TextArea = styled.textarea`
  appearance: none;
  width: 100%;
  border: none;
  outline: none;
  resize: vertical;
  min-height: 88px;
  background: transparent;
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-base);

  &::placeholder {
    color: var(--color-text-tertiary);
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
  align-items: center;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: var(--radius-pill);
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border-subtle);
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
`;

const ChipRemove = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  color: var(--color-text-tertiary);
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
  padding: 0 0 0 2px;

  &:hover {
    color: var(--color-text);
  }
`;

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
`;

const CharCount = styled.span<{ $over: boolean }>`
  font-family: var(--font-mono);
  font-size: 10px;
  color: ${(p) => (p.$over ? 'var(--color-danger)' : 'var(--color-text-tertiary)')};
`;

export const PromptComposer: React.FC<PromptComposerProps> = ({
  value,
  onChange,
  placeholder = 'Describe what you want to generate...',
  rows = 4,
  chips,
  trailing,
  charLimit,
  disabled = false,
  onSubmit,
  className,
}) => {
  const overLimit = charLimit !== undefined && value.length > charLimit;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter' && onSubmit) {
      event.preventDefault();
      onSubmit();
    }
  };

  return (
    <Root $disabled={disabled} className={className}>
      <TextArea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        onKeyDown={handleKeyDown}
      />

      {chips && chips.length > 0 && (
        <ChipRow>
          {chips.map((chip) => (
            <Chip key={chip.id}>
              {chip.icon}
              <span>{chip.label}</span>
              {chip.onRemove && (
                <ChipRemove
                  type="button"
                  aria-label={`Remove ${chip.label}`}
                  onClick={chip.onRemove}
                >
                  ×
                </ChipRemove>
              )}
            </Chip>
          ))}
        </ChipRow>
      )}

      {(trailing || charLimit !== undefined) && (
        <FooterRow>
          <div>{trailing}</div>
          {charLimit !== undefined && (
            <CharCount $over={overLimit}>
              {value.length}/{charLimit}
            </CharCount>
          )}
        </FooterRow>
      )}
    </Root>
  );
};

PromptComposer.displayName = 'PromptComposer';
